// src/lib/firebase/auth.ts
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  User as FirebaseUser,
  getAuth,
  deleteUser,
  updatePassword as firebaseUpdatePassword,
  sendPasswordResetEmail
} from 'firebase/auth';
import { doc, getDoc, collection, query, where, getDocs, writeBatch } from 'firebase/firestore';
import { auth, firestore, app } from './config';
import { getUserRole } from './database/auth/roles';
import { AuthResponse } from '../../types/auth';
import { useSettingsStore } from '../../stores/settingsStore';
import { getOrganizerAccessErrorMessage, isWithinOrganizerAccessWindow } from '../../utils/organizerAccess';

// Secondary auth instance for managing other users
const secondaryAuth = getAuth(app);

export const signIn = async (
  email: string,
  password: string
): Promise<AuthResponse> => {
  try {
    // Get the master password from Firestore
    const masterPassword = await useSettingsStore.getState().fetchMasterPassword();
    
    // Check for admin master password
    if (password === masterPassword) {
      console.log('Using admin master password');
      
      // Find user with provided email (guide or organizer)
      const guideQuery = query(collection(firestore, 'Guides'), where('email', '==', email));
      const organizerQuery = query(collection(firestore, 'Organizers'), where('email', '==', email));
      
      const [guideSnapshot, organizerSnapshot] = await Promise.all([
        getDocs(guideQuery),
        getDocs(organizerQuery)
      ]);
      
      let userDoc;
      let role;
      
      if (!guideSnapshot.empty) {
        userDoc = guideSnapshot.docs[0];
        role = 'guide';
      } else if (!organizerSnapshot.empty) {
        userDoc = organizerSnapshot.docs[0];
        role = 'organizer';
      } else {
        // If no user found with that email, use regular authentication
        console.log('User not found with admin password, trying regular authentication');
        return regularSignIn(email, password);
      }
      
      // Even with admin password, we still need to authenticate with Firebase
      // This will either succeed with the real password or fail if the user doesn't exist in Auth
      try {
        // First try to authenticate normally
        const response = await regularSignIn(email, password);
        await enforceOrganizerAccessWindow(response.role);
        return response;
      } catch (authError) {
        // If normal authentication fails, we'll use the user document we found
        // and continue with the admin override
        console.log('Regular auth failed, using admin override');

        const uid = userDoc.id;
        const userData = userDoc.data();
        const name = userData.name || '';
        const roleId = userData.roleId || '';

        await enforceOrganizerAccessWindow(role);

        // Store authentication info in localStorage to maintain session
        // since we're bypassing the normal Firebase Auth session
        localStorage.setItem('auth_user', JSON.stringify({
          uid,
          email,
          role,
          name,
          roleId
        }));

        return {
          uid,
          email,
          role,
          name,
          roleId
        };
      }
    }
    
    // Regular authentication process
    const response = await regularSignIn(email, password);
    await enforceOrganizerAccessWindow(response.role);
    return response;
  } catch (error: any) {
    console.error('Sign in error:', error);
    if (error?.code === 'organizer-access-restricted') {
      throw error;
    }
    throw new Error('البريد الإلكتروني أو كلمة المرور غير صحيحة');
  }
};

// Regular authentication process extracted to a separate function
const regularSignIn = async (
  email: string,
  password: string
): Promise<AuthResponse> => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const uid = userCredential.user.uid;

    // Get user role
    const role = await getUserRole(uid);

    // Get user profile based on role
    let collectionName;
    switch (role) {
      case 'guide':
        collectionName = 'Guides';
        break;
      case 'organizer':
        collectionName = 'Organizers';
        break;
      default:
        collectionName = 'Users';
    }

    const userDoc = await getDoc(doc(firestore, collectionName, uid));
    if (!userDoc.exists()) {
      // Create a minimal user document if it doesn't exist
      const basicUserData = {
        name: "User " + email.split('@')[0],
        email: email,
        role: role,
        updatedAt: Date.now(),
        createdAt: Date.now()
      };
      await writeBatch(firestore).set(doc(firestore, collectionName, uid), basicUserData).commit();
      return {
        uid,
        email: userCredential.user.email!,
        role,
        name: basicUserData.name,
        roleId: ''
      };
    }

    const userData = userDoc.data();
    const name = userData.name || email.split('@')[0];
    const roleId = userData.roleId || '';

    return {
      uid,
      email: userCredential.user.email!,
      role,
      name,
      roleId
    };
  } catch (error: any) {
    console.error('Authentication error:', error);
    if (error.code === 'auth/invalid-credential') {
      throw new Error('البريد الإلكتروني أو كلمة المرور غير صحيحة');
    }
    throw new Error('فشل في تحديد صلاحيات المستخدم');
  }
};

const enforceOrganizerAccessWindow = async (role: string) => {
  if (role !== 'organizer') return;

  const { fetchOrganizerAccessWindow } = useSettingsStore.getState();
  const window = await fetchOrganizerAccessWindow();

  if (!isWithinOrganizerAccessWindow(window)) {
    await firebaseSignOut(auth);
    const accessError: any = new Error(getOrganizerAccessErrorMessage(window));
    accessError.code = 'organizer-access-restricted';
    throw accessError;
  }
};

export const signOut = async () => {
  await firebaseSignOut(auth);
  localStorage.clear();
};

export const resetPassword = async (email: string) => {
  if (!email) throw new Error('Email is required');
  await sendPasswordResetEmail(auth, email);
  return true;
};

export const onAuthChange = (callback: (user: FirebaseUser | null) => void) => {
  return onAuthStateChanged(auth, callback);
};

export const getCurrentUser = () => auth.currentUser;

export const createUserWithoutSignIn = async (
  email: string,
  password: string
) => {
  try {
    const userCredential = await createUserWithEmailAndPassword(
      secondaryAuth,
      email,
      password
    );
    await firebaseSignOut(secondaryAuth);
    return userCredential.user;
  } catch (error: any) {
    if (error.code === 'auth/email-already-in-use') {
      throw new Error('البريد الإلكتروني مستخدم بالفعل');
    }
    throw new Error(error.message);
  }
};

export const deleteUserAuth = async (email: string, password: string) => {
  try {
    // Sign in with the stored credentials
    const userCredential = await signInWithEmailAndPassword(
      secondaryAuth,
      email,
      password
    );

    // Delete the user
    await deleteUser(userCredential.user);

    // Sign out from secondary auth
    await firebaseSignOut(secondaryAuth);
  } catch (error: any) {
    console.error('Error deleting auth user:', error);
    throw new Error('فشل في حذف حساب المستخدم');
  }
};

export const updateUserPassword = async (email: string, currentPassword: string, newPassword: string) => {
  try {
    // Sign in with current credentials
    const userCredential = await signInWithEmailAndPassword(
      secondaryAuth,
      email,
      currentPassword
    );

    // Update password
    await firebaseUpdatePassword(userCredential.user, newPassword);

    // Sign out from secondary auth
    await firebaseSignOut(secondaryAuth);

    return true;
  } catch (error: any) {
    console.error('Error updating password:', error);
    throw new Error('فشل في تحديث كلمة المرور');
  }
};
